<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-purple-900 to-pink-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-purple-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-pink-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-purple-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-purple-300">
            <i class="bx bx-memory-card mr-2"></i>Memory Intensive Computing
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          M3 Series Cloud <span class="text-purple-400">Instances</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          High-memory instances optimized for memory-intensive applications, large in-memory databases, real-time big data analytics, and high-performance computing workloads.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Us
          </a>
          <a routerLink="/pricing" class="bg-transparent border-2 border-purple-500 text-purple-300 hover:bg-purple-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-purchase-tag mr-2"></i>
            View Pricing
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">3.75TB</div>
            <div class="text-sm text-gray-400">Max Memory</div>
          </div>
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">DDR4</div>
            <div class="text-sm text-gray-400">ECC RAM</div>
          </div>
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">8:1</div>
            <div class="text-sm text-gray-400">RAM:vCPU Ratio</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/m3-series-hero.png" alt="M3 Series" class="w-full h-auto">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-purple-600 to-pink-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-memory-card text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">128GB</div>
              <div class="text-xs">High Memory</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-pink-600 to-purple-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-database text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">SAP HANA</div>
              <div class="text-xs">Ready</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Features Section -->
<section class="py-20 bg-slate-950 border-y border-slate-800">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-purple-500/10 border border-purple-500/40 text-purple-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        M3 Series Features
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Memory-Optimized <span class="text-purple-600">Performance</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Designed for memory-intensive workloads with high-capacity RAM and optimized configurations.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- High Memory Capacity -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-purple-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-memory-card text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">High Memory Capacity</h3>
        <p class="text-slate-300 mb-4">
          Large memory configurations with up to 3.75 TB RAM for demanding applications.
        </p>
        <div class="flex items-center text-purple-300 font-semibold">
          <i class="bx bx-chip mr-2"></i>
          <span class="text-sm">Up to 96 vCPU cores</span>
        </div>
      </div>

      <!-- Low Latency -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-pink-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-tachometer text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Low Latency</h3>
        <p class="text-slate-300 mb-4">
          Optimized for low-latency memory access with high-bandwidth configurations.
        </p>
        <div class="flex items-center text-pink-300 font-semibold">
          <i class="bx bx-transfer mr-2"></i>
          <span class="text-sm">High-bandwidth memory</span>
        </div>
      </div>

      <!-- Database Optimized -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-indigo-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-database text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Database Optimized</h3>
        <p class="text-slate-300 mb-4">
          Perfect for large in-memory databases like SAP HANA, Redis, and Memcached.
        </p>
        <div class="flex items-center text-indigo-300 font-semibold">
          <i class="bx bx-data mr-2"></i>
          <span class="text-sm">In-memory database ready</span>
        </div>
      </div>

      <!-- Big Data Analytics -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-blue-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-bar-chart-alt text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Big Data Analytics</h3>
        <p class="text-slate-300 mb-4">
          Ideal for real-time big data analytics, data warehousing, and business intelligence.
        </p>
        <div class="flex items-center text-blue-300 font-semibold">
          <i class="bx bx-brain mr-2"></i>
          <span class="text-sm">Real-time processing</span>
        </div>
      </div>

      <!-- Enterprise Security -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-red-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Enterprise Security</h3>
        <p class="text-slate-300 mb-4">
          Built-in security features with encryption and compliance certifications.
        </p>
        <div class="flex items-center text-rose-300 font-semibold">
          <i class="bx bx-lock-alt mr-2"></i>
          <span class="text-sm">Hardware encryption</span>
        </div>
      </div>

      <!-- Scalable Resources -->
      <div class="bg-slate-900/70 backdrop-blur-xl rounded-2xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300 border border-green-500/40 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-expand text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-white mb-3">Scalable Resources</h3>
        <p class="text-slate-300 mb-4">
          Easily scale memory and CPU resources to match your workload requirements.
        </p>
        <div class="flex items-center text-emerald-300 font-semibold">
          <i class="bx bx-trending-up mr-2"></i>
          <span class="text-sm">Vertical scaling</span>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Use Cases Section -->
<section class="py-20 bg-slate-950 border-b border-slate-800">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-pink-500/10 border border-pink-500/40 text-pink-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Ideal Workloads
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Perfect for <span class="text-pink-600">Memory-Intensive Applications</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        M3 Series instances excel at workloads requiring large amounts of RAM and high memory bandwidth.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- In-Memory Databases -->
      <div class="bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-purple-500/40 hover:shadow-2xl transition-all duration-300">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-pink-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-database text-3xl text-white"></i>
          </div>
          <div>
            <h3 class="text-2xl font-bold text-white mb-2">In-Memory Databases</h3>
            <p class="text-slate-300 mb-4">
              Run SAP HANA, Redis, Memcached, and other in-memory databases with massive datasets requiring fast access.
            </p>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-purple-600 mr-2"></i>
                <span class="text-sm">SAP HANA certified</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-purple-600 mr-2"></i>
                <span class="text-sm">Redis Enterprise support</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-purple-600 mr-2"></i>
                <span class="text-sm">Memcached clusters</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Big Data & Analytics -->
      <div class="bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-blue-500/40 hover:shadow-2xl transition-all duration-300">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-bar-chart-alt text-3xl text-white"></i>
          </div>
          <div>
            <h3 class="text-2xl font-bold text-white mb-2">Big Data & Analytics</h3>
            <p class="text-slate-300 mb-4">
              Process and analyze massive datasets with Apache Spark, Hadoop, and real-time analytics frameworks.
            </p>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-blue-600 mr-2"></i>
                <span class="text-sm">Apache Spark optimized</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-blue-600 mr-2"></i>
                <span class="text-sm">Hadoop ecosystem support</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-blue-600 mr-2"></i>
                <span class="text-sm">Real-time data processing</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- High-Performance Computing -->
      <div class="bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-indigo-500/40 hover:shadow-2xl transition-all duration-300">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-chip text-3xl text-white"></i>
          </div>
          <div>
            <h3 class="text-2xl font-bold text-white mb-2">High-Performance Computing</h3>
            <p class="text-slate-300 mb-4">
              Scientific computing, simulations, and computational workloads requiring extensive memory resources.
            </p>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-indigo-600 mr-2"></i>
                <span class="text-sm">Scientific simulations</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-indigo-600 mr-2"></i>
                <span class="text-sm">Computational fluid dynamics</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-indigo-600 mr-2"></i>
                <span class="text-sm">Machine learning training</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Enterprise Applications -->
      <div class="bg-white/5 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-pink-500/40 hover:shadow-2xl transition-all duration-300">
        <div class="flex items-start mb-6">
          <div class="w-16 h-16 bg-gradient-to-br from-pink-600 to-rose-700 rounded-2xl flex items-center justify-center mr-6 flex-shrink-0">
            <i class="bx bx-briefcase text-3xl text-white"></i>
          </div>
          <div>
            <h3 class="text-2xl font-bold text-white mb-2">Enterprise Applications</h3>
            <p class="text-slate-300 mb-4">
              Run memory-intensive ERP, CRM, and business applications with optimal performance.
            </p>
            <ul class="space-y-2">
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-pink-600 mr-2"></i>
                <span class="text-sm">SAP Business Suite</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-pink-600 mr-2"></i>
                <span class="text-sm">Oracle applications</span>
              </li>
              <li class="flex items-center text-slate-200">
                <i class="bx bx-check-circle text-pink-600 mr-2"></i>
                <span class="text-sm">Microsoft SQL Server</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Pricing Plans Section -->
<section class="py-20 bg-slate-950">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-purple-500/10 border border-purple-500/40 text-purple-200 px-4 py-1 rounded-full text-sm font-medium mb-4">
        M3 Series Plans
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">
        Memory-Optimized <span class="text-purple-600">Solutions</span>
      </h2>
      <p class="text-xl text-slate-300 max-w-3xl mx-auto">
        Choose the perfect memory-optimized configuration for your high-memory workloads.
      </p>
    </div>

    <div class="max-w-7xl mx-auto">
      <div class="bg-slate-900/80 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-slate-700">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="bg-gradient-to-r from-purple-600 to-pink-700 text-white">
              <tr>
                <th class="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Instance</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">vCPU</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">RAM</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">NVMe Storage</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Network</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Bandwidth</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Configuration</th>
                <th class="px-6 py-4 text-center text-sm font-bold uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-800/80">
              <!-- M3-2 -->
              <tr class="hover:bg-purple-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">M3-2</h3>
                    <p class="text-sm text-slate-300">Basic Memory</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">2</td>
                <td class="px-6 py-4 text-center">
                  <span class="text-slate-50 font-bold">16 GB</span>
                  <span class="text-xs text-purple-300 block">8:1 ratio</span>
                </td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">40 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">1 Gbps</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">2 TB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-slate-800 text-slate-100 text-xs font-bold px-3 py-1 rounded-full">Basic</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- M3-4 -->
              <tr class="hover:bg-purple-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">M3-4</h3>
                    <p class="text-sm text-slate-300">Standard Memory</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">4</td>
                <td class="px-6 py-4 text-center">
                  <span class="text-slate-50 font-bold">32 GB</span>
                  <span class="text-xs text-purple-300 block">8:1 ratio</span>
                </td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">80 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">2 Gbps</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">4 TB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-blue-600/20 text-blue-200 text-xs font-bold px-3 py-1 rounded-full">Standard</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- M3-8 (Popular) -->
              <tr class="bg-purple-900/40 border-2 border-purple-500/70">
                <td class="px-6 py-4">
                  <div class="relative">
                    <div class="absolute -top-3 -left-3">
                      <span class="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                        Most Popular
                      </span>
                    </div>
                    <h3 class="text-lg font-bold text-white mt-3">M3-8</h3>
                    <p class="text-sm text-slate-300">High Memory</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">8</td>
                <td class="px-6 py-4 text-center">
                  <span class="text-slate-50 font-bold">64 GB</span>
                  <span class="text-xs text-purple-200 block">8:1 ratio</span>
                </td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">160 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">4 Gbps</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">8 TB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-purple-500/20 text-purple-200 text-xs font-bold px-3 py-1 rounded-full">High Performance</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block shadow-lg">
                    Contact Us
                  </a>
                </td>
              </tr>

              <!-- M3-16 -->
              <tr class="hover:bg-purple-900/40 transition-colors duration-200">
                <td class="px-6 py-4">
                  <div>
                    <h3 class="text-lg font-bold text-white">M3-16</h3>
                    <p class="text-sm text-slate-300">Enterprise Memory</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-center text-slate-100 font-semibold">16</td>
                <td class="px-6 py-4 text-center">
                  <span class="text-slate-50 font-bold">128 GB</span>
                  <span class="text-xs text-purple-300 block">8:1 ratio</span>
                </td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">320 GB</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">8 Gbps</td>
                <td class="px-6 py-4 text-center text-slate-200 font-semibold">16 TB</td>
                <td class="px-6 py-4 text-center">
                  <span class="bg-indigo-500/20 text-indigo-200 text-xs font-bold px-3 py-1 rounded-full">Enterprise</span>
                </td>
                <td class="px-6 py-4 text-center">
                  <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-300 inline-block">
                    Contact Us
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <div class="bg-white/5 backdrop-blur-xl rounded-xl p-6 mt-8 text-center border border-purple-500/30">
        <div class="flex items-center justify-center text-purple-100 mb-2">
          <i class="bx bx-info-circle text-2xl mr-2"></i>
          <span class="font-semibold">All plans include 24/7 support, automatic backups, and enterprise-grade security.</span>
        </div>
        <p class="text-sm text-slate-300">Contact us for custom configurations and enterprise solutions with up to 3.75 TB RAM.</p>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-purple-600 via-pink-700 to-rose-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-pink-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Deploy High-Memory Instances?
      </h2>
      <p class="text-xl text-gray-100 mb-10">
        Get started with our M3 Series cloud instances today and experience unmatched memory performance.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-purple-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-rocket mr-2 text-xl"></i>
          <span class="text-lg">Contact Us</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-chat mr-2 text-xl"></i>
          <span class="text-lg">Talk to an Expert</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">3.75TB</div>
          <div class="text-gray-200 text-sm">Max Memory</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">8:1</div>
          <div class="text-gray-200 text-sm">RAM:vCPU</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">DDR4</div>
          <div class="text-gray-200 text-sm">ECC Memory</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">SAP</div>
          <div class="text-gray-200 text-sm">HANA Ready</div>
        </div>
      </div>
    </div>
  </div>
</section>
